import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../../lib/api';
import type { LocationTypeItem } from '../location/types';

/**
 * 位置类型管理（架构书 §11.3："系统设置、原因码、单位、位置类型"归
 * `settings.manage`）。只在持有该权限时渲染，与其它 Settings 面板同一模式。
 */
export function LocationTypesPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data, isPending } = useQuery({
    queryKey: ['location-types'],
    queryFn: () => apiFetch<{ items: LocationTypeItem[] }>('/location-types'),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [code, setCode] = useState('');
  const [nameKey, setNameKey] = useState('');
  const [defaultCanStore, setDefaultCanStore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const create = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch('/location-types', {
        method: 'POST',
        body: { code, nameKey, defaultCanStore },
      });
      setShowCreate(false);
      setCode('');
      setNameKey('');
      setDefaultCanStore(true);
      await queryClient.invalidateQueries({ queryKey: ['location-types'] });
    } catch (err) {
      setError(err instanceof ApiError ? t(`errors:${err.code}`, err.params) : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="section">
      <div className="row row--between">
        <h2>{t('locationType.title')}</h2>
        <button className="btn btn--primary btn--sm" onClick={() => setShowCreate(true)}>
          {t('locationType.create')}
        </button>
      </div>

      {isPending && <p>{t('app.loading')}</p>}
      {data && (
        <div className="table-scroll"><table className="table">
          <thead>
            <tr>
              <th>{t('locationType.colCode')}</th>
              <th>{t('locationType.colName')}</th>
              <th>{t('locationType.colDefaultCanStore')}</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((type) => (
              <tr key={type.id}>
                <td>{type.code}</td>
                <td>{t(type.nameKey, type.code)}</td>
                <td>{type.defaultCanStore ? '✓' : ''}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}

      {showCreate && (
        <div className="modal-overlay" onClick={() => setShowCreate(false)}>
          <form
            className="modal"
            onClick={(e) => e.stopPropagation()}
            onSubmit={(e) => void create(e)}
          >
            <h2>{t('locationType.create')}</h2>
            <label className="field">
              <span>{t('locationType.colCode')}</span>
              <input value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
            </label>
            <label className="field">
              <span>{t('locationType.colName')}</span>
              <input
                value={nameKey}
                onChange={(e) => setNameKey(e.target.value)}
                required
                placeholder="locationType.custom.coldRoom"
              />
            </label>
            <label className="row" style={{ marginBottom: 'var(--space-4)' }}>
              <input
                type="checkbox"
                checked={defaultCanStore}
                onChange={(e) => setDefaultCanStore(e.target.checked)}
              />
              {t('locationType.colDefaultCanStore')}
            </label>
            {error && <p className="field-error">{error}</p>}
            <div className="modal__actions">
              <button type="button" className="btn btn--ghost" onClick={() => setShowCreate(false)}>
                {t('app.cancel')}
              </button>
              <button type="submit" className="btn btn--primary" disabled={submitting}>
                {t('locationType.create')}
              </button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
